/* je-postcode-journey-20260825
 * One-session postcode handoff from product/cart to Shopify checkout.
 * Full postcodes never enter analytics; the browser retains one only in
 * sessionStorage and Shopify receives it only when the customer checks out.
 */
(() => {
  if (window.__jePostcodeJourneyLoaded) return;
  window.__jePostcodeJourneyLoaded = true;

  const STORAGE_KEY = 'je_delivery_postcode_session_v1';
  let zonesPromise;

  const parsePostcode = (value) => {
    const compact = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (compact === 'GIR0AA') return { postcode: 'GIR 0AA', outcode: 'GIR' };
    const match = compact.match(/^([A-Z]{1,2}[0-9][A-Z0-9]?)([0-9][A-Z]{2})$/);
    return match ? { postcode: `${match[1]} ${match[2]}`, outcode: match[1] } : null;
  };

  const readStored = () => {
    try {
      const value = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      return value && parsePostcode(value.postcode) ? value : null;
    } catch (_error) {
      return null;
    }
  };

  const storeChecked = (result) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        postcode: result.postcode,
        outcode: result.outcode,
        status: result.status,
      }));
    } catch (_error) {
      // Checkout prefill still works through its hidden form field.
    }
    /* je-postcode-check-event-20261004: outcode + status only; full postcodes never
       enter analytics (see file header). GA4 consent mode gates the send. */
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'postcode_check', { outcode: result.outcode, status: result.status });
      }
    } catch (_error) { /* analytics must never block the checkout postcode gate */ }
    document.dispatchEvent(new CustomEvent('johns:postcode-checked', {
      detail: { outcode: result.outcode, status: result.status },
    }));
  };

  const zonesUrl = () => {
    const cartPanel = document.querySelector('[data-je-cart-postcode][data-zones-url]');
    if (cartPanel?.dataset.zonesUrl) return cartPanel.dataset.zonesUrl;
    const pdpUrl = document.getElementById('jeDeliveryZonesUrl');
    if (!pdpUrl) return '';
    try { return JSON.parse(pdpUrl.textContent); } catch (_error) { return ''; }
  };

  const loadZones = () => {
    if (zonesPromise) return zonesPromise;
    const url = zonesUrl();
    if (!url) return Promise.reject(new Error('Delivery zones URL is unavailable'));
    zonesPromise = fetch(url, { credentials: 'same-origin' }).then((response) => {
      if (!response.ok) throw new Error(`Delivery zones returned ${response.status}`);
      return response.json();
    });
    return zonesPromise;
  };

  const classify = async (postcode) => {
    const parsed = parsePostcode(postcode);
    if (!parsed) return { status: 'invalid' };
    const zones = await loadZones();
    const entry = zones.districts?.[parsed.outcode];
    return {
      ...parsed,
      status: entry?.delivery === true ? 'eligible' : 'collection',
      area: entry?.area || '',
    };
  };

  const cartScope = (element) =>
    element.closest('.cart-summary__inner, .cart-drawer, dialog-component') || document;

  const cartParts = (scope) => ({
    panel: scope.querySelector('[data-je-cart-postcode]'),
    input: scope.querySelector('[data-je-cart-postcode-input]'),
    result: scope.querySelector('[data-je-cart-postcode-result]'),
    hidden: scope.querySelector('[data-je-checkout-postcode]'),
    ctas: scope.querySelector('[data-je-postcode-ctas]'),
  });

  const setCartState = (scope, result, message) => {
    const parts = cartParts(scope);
    if (!parts.panel || !parts.result) return;
    const ready = ['eligible', 'collection', 'unconfirmed'].includes(result.status);
    parts.panel.dataset.checked = ready ? 'true' : 'false';
    parts.panel.dataset.state = result.status;
    parts.result.hidden = false;
    parts.result.dataset.state = result.status;
    parts.result.textContent = message;
    if (parts.hidden) parts.hidden.value = result.status === 'eligible' ? result.postcode : '';
    if (parts.ctas) {
      parts.ctas.dataset.postcodeReady = ready ? 'true' : 'false';
      parts.ctas.dataset.postcodeEligible = result.status === 'eligible' ? 'true' : 'false';
    }
    if (ready) storeChecked(result);
  };

  const checkCart = async (form) => {
    const scope = cartScope(form);
    const parts = cartParts(scope);
    const parsed = parsePostcode(parts.input?.value);
    if (!parsed) {
      setCartState(scope, { status: 'invalid' }, 'Enter a full UK postcode, for example DN15 6EN.');
      parts.input?.focus();
      return;
    }
    if (parts.input) parts.input.value = parsed.postcode;
    try {
      const result = await classify(parsed.postcode);
      if (result.status === 'eligible') {
        const area = result.area ? ` for ${result.area}` : '';
        setCartState(scope, result, `Local delivery is available${area}, free on orders of \u00a3100 or more (\u00a315 below that). We’ll carry ${result.postcode} into checkout.`);
      } else {
        setCartState(scope, result, `Delivery isn’t available to ${result.outcode}, but you can continue to checkout for free collection from 142 High Street, Scunthorpe.`);
      }
    } catch (_error) {
      setCartState(scope, { ...parsed, status: 'unconfirmed' }, 'We couldn’t confirm the delivery area just now. Continue to checkout, where your full address is the final delivery check, or call 01724 844040.');
    }
  };

  const hydrateCart = () => {
    const stored = readStored();
    document.querySelectorAll('[data-je-cart-postcode]').forEach((panel) => {
      if (panel.dataset.jeHydrated === 'true') return;
      panel.dataset.jeHydrated = 'true';
      const scope = cartScope(panel);
      const parts = cartParts(scope);
      if (stored && parts.input) {
        parts.input.value = stored.postcode;
        const form = parts.input.closest('form');
        if (form) checkCart(form);
      }
    });
  };

  const hydrateProduct = () => {
    const form = document.getElementById('jePostcodeForm');
    const input = document.getElementById('jePostcodeInput');
    if (!form || !input || form.dataset.jeJourneyHydrated === 'true') return;
    form.dataset.jeJourneyHydrated = 'true';
    const stored = readStored();
    if (stored && !input.value) input.value = stored.postcode;
    form.addEventListener('submit', () => {
      const parsed = parsePostcode(input.value);
      if (!parsed) return;
      input.value = parsed.postcode;
      classify(parsed.postcode).then(storeChecked).catch(() => {});
    });
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('[data-je-cart-postcode-form]');
    if (!form) return;
    event.preventDefault();
    checkCart(form);
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-je-postcode-checkout-button]');
    if (!button) return;
    const scope = cartScope(button);
    const parts = cartParts(scope);
    if (parts.panel?.dataset.checked !== 'true') {
      event.preventDefault();
      event.stopPropagation();
      setCartState(scope, { status: 'invalid' }, 'Check your postcode before continuing to checkout.');
      parts.input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      parts.input?.focus({ preventScroll: true });
      return;
    }
    if (parts.panel.dataset.state === 'eligible' && parts.hidden?.value) {
      // Shopify ignores checkout address fields in the ordinary cart POST, but
      // accepts this same documented checkout parameter on the current-cart URL.
      event.preventDefault();
      event.stopPropagation();
      const checkoutUrl = new URL('/checkout', window.location.origin);
      checkoutUrl.searchParams.set('checkout[shipping_address][zip]', parts.hidden.value);
      window.location.assign(checkoutUrl.toString());
    }
  }, true);

  document.addEventListener('cart:update', () => setTimeout(hydrateCart, 0));
  hydrateProduct();
  hydrateCart();

  window.JohnsPostcodeJourney = Object.freeze({ parsePostcode, classify });
})();
